import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Target, DollarSign } from 'lucide-react';
import Card from '../../../components/base/Card';
import type { EstadoPresupuesto } from '../../../shared/types/presupuestos';

interface ResumenPresupuestosProps {
  estados: EstadoPresupuesto[];
  className?: string;
}

export const ResumenPresupuestos: React.FC<ResumenPresupuestosProps> = ({
  estados,
  className = ''
}) => {
  // Calcular estadísticas generales
  const estadisticas = React.useMemo(() => {
    const total = estados.length;
    const normales = estados.filter(e => e.estado === 'normal').length;
    const advertencias = estados.filter(e => e.estado === 'advertencia').length;
    const superados = estados.filter(e => e.estado === 'superado').length;
    
    const totalPresupuestado = estados.reduce((acc, e) => acc + e.presupuesto.monto, 0);
    const totalGastado = estados.reduce((acc, e) => acc + e.gastoActual, 0);
    const porcentajeGlobal = totalPresupuestado > 0 ? (totalGastado / totalPresupuestado) * 100 : 0;
    
    return {
      total,
      normales,
      advertencias,
      superados,
      totalPresupuestado,
      totalGastado,
      porcentajeGlobal,
      disponible: totalPresupuestado - totalGastado
    };
  }, [estados]);

  const formatearMoneda = (monto: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(monto);
  };

  const getStatusColor = () => {
    if (estadisticas.porcentajeGlobal >= 100) return 'text-red-600';
    if (estadisticas.porcentajeGlobal >= 80) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = () => {
    if (estadisticas.porcentajeGlobal >= 100) return <TrendingDown className="w-5 h-5 text-red-600" />;
    if (estadisticas.porcentajeGlobal >= 80) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    return <TrendingUp className="w-5 h-5 text-green-600" />;
  };

  if (estados.length === 0) {
    return (
      <Card className={`p-6 text-center ${className}`}>
        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No hay presupuestos configurados
        </h3>
        <p className="text-gray-600">
          Crea tu primer presupuesto para comenzar a controlar tus gastos.
        </p>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Resumen general */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Resumen de Presupuestos
          </h3>
          {getStatusIcon()}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {formatearMoneda(estadisticas.totalPresupuestado)}
            </p>
            <p className="text-sm text-gray-600">Presupuestado</p>
          </div>
          
          <div className="text-center">
            <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {formatearMoneda(estadisticas.totalGastado)}
            </p>
            <p className="text-sm text-gray-600">Gastado</p>
          </div>
          
          <div className="text-center">
            <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className={`text-2xl font-bold ${getStatusColor()}`}>
              {estadisticas.porcentajeGlobal.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-600">Ejecutado</p>
          </div>
          
          <div className="text-center">
            <TrendingDown className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <p className={`text-2xl font-bold ${estadisticas.disponible >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatearMoneda(estadisticas.disponible)}
            </p>
            <p className="text-sm text-gray-600">Disponible</p>
          </div>
        </div>

        {/* Barra de progreso global */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progreso Global</span>
            <span>{estadisticas.porcentajeGlobal.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${
                estadisticas.porcentajeGlobal >= 100 
                  ? 'bg-red-500' 
                  : estadisticas.porcentajeGlobal >= 80 
                    ? 'bg-yellow-500' 
                    : 'bg-green-500'
              }`}
              style={{ 
                width: `${Math.min(estadisticas.porcentajeGlobal, 100)}%` 
              }}
            />
          </div>
        </div>
      </Card>

      {/* Estadísticas por estado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-green-700">
                {estadisticas.normales}
              </p>
              <p className="text-sm text-green-600">En Control</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-yellow-700">
                {estadisticas.advertencias}
              </p>
              <p className="text-sm text-yellow-600">En Advertencia</p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-red-700">
                {estadisticas.superados}
              </p>
              <p className="text-sm text-red-600">Superados</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};